// AUTO-GENERATED — DO NOT EDIT.
//
// Stable identifiers for HTTPException detail strings the frontend
// pattern-matches on. Frontend matchers that compare against any of
// these strings MUST import the constant here so the matcher stays
// in lockstep with the backend emitter.
//
// Source of truth: docs/api-errors.yaml.
// Regenerate with: python scripts/gen-error-constants.py
// CI gate: .github/workflows/ci-codegen-drift.yml

/**
 * Registry id: auth_missing_bearer
 *
 * Generic "no Authorization header" / malformed Bearer prefix
 * case. The frontend's stale-token recovery deliberately does
 * NOT fire on this string (there's no token to invalidate), and
 * ``V3/frontend/src/api/client.test.ts`` asserts that
 * non-firing-ness explicitly — so the exact text is part of the
 * coupled contract even though no matcher branch fires on it.
 */
export const AUTH_MISSING_BEARER = "Missing bearer token" as const;

/**
 * Registry id: auth_student_token_revoked
 *
 * Raised when the JWT signature verified but no matching hash
 * row exists in ``student_session_tokens`` — teacher-side
 * revocation (PIPA P3 ``DELETE /api/teacher/me``) or a token
 * that was never issued. Frontend's ``STALE_STUDENT_TOKEN_PATTERNS``
 * substring ``'revoked'`` catches this — recovery fires:
 * wipe student session + redirect to splash or /dev/quickstart.
 */
export const AUTH_STUDENT_TOKEN_REVOKED = "Token revoked or unknown" as const;

/**
 * Registry id: auth_student_token_expired
 *
 * Raised when the ``student_session_tokens.expires_at`` row is
 * past — separate from the JWT's own ``exp`` claim because the
 * DB row is the authoritative server-side TTL (teachers may
 * kill a token early). Frontend ``'expired'`` substring catches
 * this.
 */
export const AUTH_STUDENT_TOKEN_EXPIRED = "Token expired" as const;

/**
 * Registry id: auth_student_not_found
 *
 * Raised when the student row referenced by an otherwise-valid
 * token was soft-deleted between token issuance and the current
 * request. Used by multiple call sites (auth dep, classes
 * router student delete branch, admin_students containment
 * checks) — all converging on this exact text so the frontend
 * substring matcher covers every surface.
 */
export const AUTH_STUDENT_NOT_FOUND = "Student not found" as const;

/**
 * Registry id: auth_invalid_student_token_prefix
 *
 * Prefix-only — backend f-strings append a dynamic suffix.
 *
 * Static head of an f-string whose dynamic tail is the underlying
 * ``jwt.InvalidTokenError`` message — full message looks like
 * ``"Invalid student token: Signature verification failed"``.
 * Backend emits as ``f"{AUTH_INVALID_STUDENT_TOKEN_PREFIX}{e}"``;
 * the frontend's ``'student token'`` substring catches the
 * prefix and fires recovery.
 */
export const AUTH_INVALID_STUDENT_TOKEN_PREFIX = "Invalid student token: " as const;

/**
 * Registry id: dev_invalid_quickstart_password
 *
 * Returned by every ``/api/dev/quickstart/*`` endpoint when
 * ``X-Dev-Password`` doesn't match the configured
 * ``DEV_QUICKSTART_PASSWORD``. The e2e helper at
 * ``V3/frontend/e2e/helpers.ts:84`` does
 * ``body.includes(DEV_INVALID_QUICKSTART_PASSWORD)`` to surface
 * a richer "the e2e password isn't set in CI / wrong secret"
 * hint to the test author instead of a generic 401 stack.
 */
export const DEV_INVALID_QUICKSTART_PASSWORD = "Invalid quickstart password" as const;

/**
 * Registry id: session_version_conflict
 *
 * ``error_code`` body-field value on the 409 emitted by main.py's
 * ``stale_data_handler`` for a #106 optimistic-version conflict on
 * the ``sessions`` row. This is the ONLY 409 that should surface the
 * "updated elsewhere — reload" dialog. The frontend's
 * ``isStaleVersionConflict`` matcher (client.ts) compares
 * ``ApiError.code`` against this constant exactly.
 */
export const SESSION_VERSION_CONFLICT = "session_version_conflict" as const;

/**
 * Registry id: advance_in_progress
 *
 * ``error_code`` body-field value on the idempotency-in-flight 409
 * raised by ``services/idempotency.py::check_or_register`` when a
 * request with the same ``Idempotency-Key`` is still being processed.
 * For ``/advance`` this means another in-flight call is already
 * running the transition; the loser should retry (the retry hits the
 * status-gated no-op branch and observes ``tutoring``), NOT pop the
 * reload dialog. The frontend's ``isAdvanceInProgress`` matcher
 * (client.ts) compares ``ApiError.code`` against this constant.
 */
export const ADVANCE_IN_PROGRESS = "advance_in_progress" as const;

/**
 * Registry id: session_corrupted
 *
 * ``error_code`` body-field value on the 409 the STREAMING endpoint
 * ``POST /{slug}/message-stream`` returns when the session is (or
 * becomes) ``corrupted`` (#108) — SSE can't deliver "this session is
 * broken" mid-stream, so it rejects pre-stream as 409 and the
 * frontend refreshes into the recovery UI. The non-streaming paths
 * (``/message``, ``/advance``, ``/end``) instead return 200 with
 * ``status="corrupted"`` and never raise this. Carries the code so
 * the frontend's ``handleStaleSessionConflict`` keeps driving the
 * refresh→recovery flow AFTER #977 narrowed it from any-409 to
 * specific codes — without this, the corrupted 409 silently fell
 * through to a raw ``alert()``. The ``isSessionCorrupted`` matcher
 * (client.ts) compares ``ApiError.code`` against this constant.
 */
export const SESSION_CORRUPTED = "session_corrupted" as const;

/**
 * Registry id: agreement_version_stale
 *
 * ``error_code`` on the 409 from ``POST /api/me/agreements/accept``
 * when the accepted version is not the current one (ADR 0080).
 * Matched by the frontend's ``isAgreementVersionStale``.
 */
export const AGREEMENT_VERSION_STALE = "agreement_version_stale" as const;
